import { Controller } from "@hotwired/stimulus";

/**
 * "Have we served this number before?" (CAL-499).
 *
 * Watches the builder's phone box and points the match
 * frame at the lookup endpoint, debounced, so a merchant
 * typing ten digits asks once rather than ten times.
 *
 * A Turbo frame navigation rather than a `fetch`: the
 * endpoint answers with the match card, which IS the
 * frame, so Turbo already knows how to put it on screen.
 * That also means there is no request of ours to hang: a
 * frame load that never comes back leaves the strip
 * empty, which is the same thing an unrecognised number
 * shows, and nothing on this page is waiting on it.
 *
 * The server decides what a match IS. This controller
 * only refuses to ask about a fragment: `MIN_DIGITS` is
 * short enough that no real Mexican mobile is withheld
 * and long enough that the first keystroke does not send
 * a request matching a third of the customer list.
 */
const MIN_DIGITS = 8;
const DEBOUNCE_MS = 350;

export default class extends Controller {
  static targets = ["phone", "frame", "userId"];
  static values = { url: String };

  disconnect() {
    this.cancelPending();
  }

  lookup() {
    this.detach();
    this.cancelPending();
    this.timer = setTimeout(
      () => this.ask(),
      DEBOUNCE_MS
    );
  }

  /**
   * THE ATTACHMENT ONLY HOLDS WHILE THE NUMBER THAT
   * PRODUCED IT IS STILL IN THE BOX.
   *
   * The hidden `user_id` is what files a manual order
   * under a customer, and it used to be written by the
   * server alone: rendered from the resolved user, and
   * changed only by "Use" navigating. So a merchant who
   * matched customer A, pressed Use, realised it was the
   * wrong person and retyped B's number submitted an
   * order carrying A's id with B's phone. The lookup
   * fires and B's card appears, so the screen shows B
   * while the field still says A, and nothing on the page
   * can reveal it.
   *
   * That misfiles the order under A, and once a loyalty
   * tier reaches the totals it misprices it too: A's perk
   * applied to B's order, with the preview and the
   * created order agreeing with each other and both wrong
   * about whose order it is.
   *
   * Cleared on EVERY keystroke, including one that puts
   * the same number back. Between deleting a digit and
   * retyping it the attachment was not true, and a field
   * that survives that is a field whose meaning drifts
   * from what the screen shows. Pressing "Use" on the
   * match card re-attaches, which is a full navigation
   * and so states it from the server again.
   *
   * Before the debounce, not inside it: the attachment is
   * wrong the moment the number is, and a merchant who
   * submits within the window must not carry the old one.
   */
  detach() {
    if (!this.hasUserIdTarget) return;

    this.userIdTarget.value = "";
  }

  cancelPending() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  ask() {
    if (!this.hasFrameTarget) return;
    if (!this.hasPhoneTarget) return;

    const typed = this.phoneTarget.value || "";
    if (this.digits(typed).length < MIN_DIGITS) {
      this.clear();
      return;
    }

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set("phone", typed);
    this.frameTarget.src = url.toString();
  }

  /**
   * Back to no match at all, which is what a number we
   * have never seen shows. Emptying the frame rather
   * than removing it: it has to still be there for the
   * next answer to land in.
   */
  clear() {
    this.frameTarget.removeAttribute("src");
    this.frameTarget.innerHTML = "";
  }

  digits(value) {
    return value.replace(/\D/g, "");
  }
}
